import { get } from '@ember/object';
import changesetGet from 'ember-changeset/helpers/changeset-get';
import ScaleSurveyEditionFormComponent from 'parog-web/components/abstract/scale-survey-edition-form';
import type { Payload } from 'parog-web/components/forc/abstract/journal-event-based-component';
import Provider from 'parog-web/components/forc/provider';
import QidsDeclaredJournalEvent from 'parog-web/models/qids-declared-journal-event';
import qidsScoreDescription from 'parog-web/utils/qids-score-description';
import TwoButtonTabs from 'ui-kit/components/two-button-tabs';

export default class QidsEditionFormComponent extends ScaleSurveyEditionFormComponent {
  static override forcDisplayName =
    'Quick Inventory of Depressive Symptomatology (QIDS)';
  static override fullComponentId = 'scale-surveys/qids-edition-form';
  static override journalEventClass = QidsDeclaredJournalEvent;

  override get maximalScore() {
    return 27;
  }

  override get numberOfItems() {
    return 16;
  }

  override get validationNames() {
    return [
      "L'insomnie d'endormissement",
      "L'insomnie du milieu de la nuit",
      "L'insomnie du petit matin",
      "L'hypersomnie",
      "L'humeur",
      "L'appétit (diminution)",
      "L'appétit (augmentation)",
      'Le poids (perte)',
      'Le poids (augmentation)',
      'La concentration / prise de décision',
      "L'image de soi",
      "L'idéation suicidaire",
      "L'implication dans les activités",
      "L'énergie / fatigabilité",
      'Le ralentissement psychomoteur',
      "L'agitation psychomotrice"
    ];
  }

  // payload will either be a changeset (when template refreshes),
  // or a POJO (when the form is saved).
  override _computeScore(payload: Payload) {
    /* eslint-disable @typescript-eslint/no-this-alias */
    const self = this;
    function getValue(i: number) {
      const key = self.questionIds[i - 1];
      return parseInt(get(payload, key) as string);
    }

    // Score formula :
    // (Maximum de (Q1, Q2, Q3, Q4)) + Q5 + (Maximum de (Q6, Q7, Q8, Q9)) + Q10 + Q11 + Q12 + Q13 + Q14 + (Maximum de (Q15, Q16))
    const number =
      Math.max(getValue(1), getValue(2), getValue(3), getValue(4)) +
      getValue(5) +
      Math.max(getValue(6), getValue(7), getValue(8), getValue(9)) +
      getValue(10) +
      getValue(11) +
      getValue(12) +
      getValue(13) +
      getValue(14) +
      Math.max(getValue(15), getValue(16));

    return {
      number,
      description: qidsScoreDescription(number)
    };
  }

  <template>
    <Provider @forcComponent={{this}}>
      <:content as |Form|>
        <TwoButtonTabs
          class='large'
          @selected={{if
            (changesetGet this.changeset 'isTotalScoreFilled')
            'second'
            'first'
          }}
          @onClick={{this.onTabButtonClicked}}
        >
          <:firstButton>Répondre aux questions</:firstButton>
          <:secondButton>Renseigner le score</:secondButton>

          <:firstTab>
            <div class='field'>
              <Form.Date
                @labelName='Date à laquelle le questionnaire a été renseigné'
                @propertyName='occurredAt'
              />
            </div>

            <h4>Pour chaque item, sélectionnez la réponse qui décrit le mieux le
              patient au cours des sept derniers jours.</h4>

            <div class='field'>
              <Form.FieldsRadioGroup
                @title="1. Insomnie d'endormissement"
                @propertyName='0'
                as |Group|
              >
                <Group.FieldRadio
                  @value='0'
                  @label="Ne met jamais plus de 30 minutes pour s'endormir."
                />
                <Group.FieldRadio
                  @value='1'
                  @label="Moins d'une fois sur deux, met au moins 30 minutes pour s'endormir."
                />
                <Group.FieldRadio
                  @value='2'
                  @label="Plus d'une fois sur deux, met au moins 30 minutes pour s'endormir."
                />
                <Group.FieldRadio
                  @value='3'
                  @label="Plus d'une fois sur deux, met plus de 60 minutes pour s'endormir."
                />
              </Form.FieldsRadioGroup>
            </div>

            <div class='field'>
              <Form.FieldsRadioGroup
                @title='2. Insomnie du milieu de la nuit'
                @propertyName='1'
                as |Group|
              >
                <Group.FieldRadio
                  @value='0'
                  @label='Ne se réveille pas la nuit.'
                />
                <Group.FieldRadio
                  @value='1'
                  @label='Agitation, sommeil léger avec peu de réveils.'
                />
                <Group.FieldRadio
                  @value='2'
                  @label='Se réveille au moins une fois par nuit mais se rendort facilement.'
                />
                <Group.FieldRadio
                  @value='3'
                  @label="Plus d'une fois sur deux, se réveille plus d'une fois par nuit et reste éveillé pendant 20 minutes ou plus."
                />
              </Form.FieldsRadioGroup>
            </div>

            <div class='field'>
              <Form.FieldsRadioGroup
                @title='3. Insomnie du petit matin'
                @propertyName='2'
                as |Group|
              >
                <Group.FieldRadio
                  @value='0'
                  @label="Moins d'une fois sur deux, ne se réveille pas plus de 30 minutes avant de devoir se lever."
                />
                <Group.FieldRadio
                  @value='1'
                  @label="Plus d'une fois sur deux, se réveille plus de 30 minutes avant de devoir se lever."
                />
                <Group.FieldRadio
                  @value='2'
                  @label="Plus d'une fois sur deux, se réveille au moins 1 heure avant de devoir se lever."
                />
                <Group.FieldRadio
                  @value='3'
                  @label="Plus d'une fois sur deux, se réveille au moins 2 heures avant de devoir se lever."
                />
              </Form.FieldsRadioGroup>
            </div>

            <div class='field'>
              <Form.FieldsRadioGroup
                @title='4. Hypersomnie'
                @propertyName='3'
                as |Group|
              >
                <Group.FieldRadio
                  @value='0'
                  @label='Ne dort pas plus de 7-8 heures par nuit et ne fait pas de sieste.'
                />
                <Group.FieldRadio
                  @value='1'
                  @label='Ne dort pas plus de 10 heures par période de 24 heures (sieste comprise).'
                />
                <Group.FieldRadio
                  @value='2'
                  @label='Ne dort pas plus de 12 heures par période de 24 heures (sieste comprise).'
                />
                <Group.FieldRadio
                  @value='3'
                  @label='Dort plus de 12 heures par période de 24 heures (sieste comprise).'
                />
              </Form.FieldsRadioGroup>
            </div>

            <div class='field'>
              <Form.FieldsRadioGroup
                @title='5. Humeur (tristesse)'
                @propertyName='4'
                as |Group|
              >
                <Group.FieldRadio @value='0' @label='Ne se sent pas triste.' />
                <Group.FieldRadio
                  @value='1'
                  @label='Se sent triste moins de la moitié du temps.'
                />
                <Group.FieldRadio
                  @value='2'
                  @label='Se sent triste plus de la moitié du temps.'
                />
                <Group.FieldRadio
                  @value='3'
                  @label='Se sent extrêmement triste pratiquement tout le temps.'
                />
              </Form.FieldsRadioGroup>
            </div>

            <div class='field'>
              <Form.FieldsRadioGroup
                @title='6. Appétit (diminution)'
                @propertyName='5'
                as |Group|
              >
                <Group.FieldRadio
                  @value='0'
                  @label="Pas de changement par rapport à l'appétit habituel."
                />
                <Group.FieldRadio
                  @value='1'
                  @label="Mange plutôt moins souvent et/ou de plus petites quantités que d'habitude."
                />
                <Group.FieldRadio
                  @value='2'
                  @label="Mange beaucoup moins que d'habitude et seulement en se forçant."
                />
                <Group.FieldRadio
                  @value='3'
                  @label="Mange rarement dans une période de 24 heures et seulement au prix de gros efforts ou lorsqu'on l'en persuade."
                />
              </Form.FieldsRadioGroup>
            </div>

            <div class='field'>
              <Form.FieldsRadioGroup
                @title='7. Appétit (augmentation)'
                @propertyName='6'
                as |Group|
              >
                <Group.FieldRadio
                  @value='0'
                  @label="Pas de changement par rapport à l'appétit habituel."
                />
                <Group.FieldRadio
                  @value='1'
                  @label="Éprouve plus souvent que d'habitude le besoin de manger."
                />
                <Group.FieldRadio
                  @value='2'
                  @label="Mange régulièrement plus souvent et/ou en plus grandes quantités que d'habitude."
                />
                <Group.FieldRadio
                  @value='3'
                  @label='Se sent poussé à trop manger pendant et entre les repas.'
                />
              </Form.FieldsRadioGroup>
            </div>

            <div class='field'>
              <Form.FieldsRadioGroup
                @title='8. Poids (perte) au cours des deux dernières semaines'
                @propertyName='7'
                as |Group|
              >
                <Group.FieldRadio
                  @value='0'
                  @label='Ne pense pas avoir changé de poids.'
                />
                <Group.FieldRadio
                  @value='1'
                  @label="A l'impression d'avoir perdu un peu de poids."
                />
                <Group.FieldRadio @value='2' @label='A perdu 1 kg ou plus.' />
                <Group.FieldRadio @value='3' @label='A perdu 2,5 kg ou plus.' />
              </Form.FieldsRadioGroup>
            </div>

            <div class='field'>
              <Form.FieldsRadioGroup
                @title='9. Poids (augmentation) au cours des deux dernières semaines'
                @propertyName='8'
                as |Group|
              >
                <Group.FieldRadio
                  @value='0'
                  @label='Ne pense pas avoir changé de poids.'
                />
                <Group.FieldRadio
                  @value='1'
                  @label="A l'impression d'avoir un peu grossi."
                />
                <Group.FieldRadio @value='2' @label='A pris 1 kg ou plus.' />
                <Group.FieldRadio @value='3' @label='A pris 2,5 kg ou plus.' />
              </Form.FieldsRadioGroup>
            </div>

            <div class='field'>
              <Form.FieldsRadioGroup
                @title='10. Concentration / prise de décision'
                @propertyName='9'
                as |Group|
              >
                <Group.FieldRadio
                  @value='0'
                  @label='Pas de changement dans les capacités de concentration et de prise de décision.'
                />
                <Group.FieldRadio
                  @value='1'
                  @label='Se sent parfois indécis ou trouve que son attention se disperse.'
                />
                <Group.FieldRadio
                  @value='2'
                  @label='La plupart du temps, lutte pour fixer son attention ou pour prendre une décision.'
                />
                <Group.FieldRadio
                  @value='3'
                  @label='Est incapable de se concentrer suffisamment pour lire ou est incapable de prendre une décision même peu importante.'
                />
              </Form.FieldsRadioGroup>
            </div>

            <div class='field'>
              <Form.FieldsRadioGroup
                @title='11. Image de soi'
                @propertyName='10'
                as |Group|
              >
                <Group.FieldRadio
                  @value='0'
                  @label="Considère qu'il/elle a autant de valeur et qu'il/elle est aussi estimable que les autres."
                />
                <Group.FieldRadio
                  @value='1'
                  @label="Se fait plus de reproches que d'habitude."
                />
                <Group.FieldRadio
                  @value='2'
                  @label="Croit très souvent qu'il/elle cause des problèmes aux autres."
                />
                <Group.FieldRadio
                  @value='3'
                  @label='Rumine à propos de ses gros et de ses petits défauts.'
                />
              </Form.FieldsRadioGroup>
            </div>

            <div class='field'>
              <Form.FieldsRadioGroup
                @title='12. Idéation suicidaire'
                @propertyName='11'
                as |Group|
              >
                <Group.FieldRadio
                  @value='0'
                  @label='Ne pense pas au suicide ou à la mort.'
                />
                <Group.FieldRadio
                  @value='1'
                  @label="Sentiment que la vie est vide ou qu'elle ne vaut pas la peine d'être vécue."
                />
                <Group.FieldRadio
                  @value='2'
                  @label='Pense au suicide ou à la mort plusieurs fois par semaine pendant plusieurs minutes.'
                />
                <Group.FieldRadio
                  @value='3'
                  @label='Pense sérieusement au suicide ou à la mort plusieurs fois par jour, ou a préparé son suicide ou a essayé de mettre fin à ses jours.'
                />
              </Form.FieldsRadioGroup>
            </div>

            <div class='field'>
              <Form.FieldsRadioGroup
                @title='13. Implication dans les activités'
                @propertyName='12'
                as |Group|
              >
                <Group.FieldRadio
                  @value='0'
                  @label="Pas de changement dans le niveau habituel d'intérêt pour les autres et pour les activités."
                />
                <Group.FieldRadio
                  @value='1'
                  @label="Remarque une diminution des intérêts/activités qu'il/elle avait auparavant."
                />
                <Group.FieldRadio
                  @value='2'
                  @label="Seuls un ou deux intérêts qu'il/elle avait auparavant subsistent."
                />
                <Group.FieldRadio
                  @value='3'
                  @label="N'a pratiquement plus aucun intérêt pour les activités pratiquées auparavant."
                />
              </Form.FieldsRadioGroup>
            </div>

            <div class='field'>
              <Form.FieldsRadioGroup
                @title='14. Énergie / fatigabilité'
                @propertyName='13'
                as |Group|
              >
                <Group.FieldRadio
                  @value='0'
                  @label="Pas de changement par rapport au niveau d'énergie habituel."
                />
                <Group.FieldRadio
                  @value='1'
                  @label="Se fatigue plus facilement que d'habitude."
                />
                <Group.FieldRadio
                  @value='2'
                  @label='Doit faire des efforts significatifs pour entreprendre ou poursuivre les activités quotidiennes habituelles.'
                />
                <Group.FieldRadio
                  @value='3'
                  @label="Ne peut mener à bien la plupart des activités quotidiennes par manque d'énergie."
                />
              </Form.FieldsRadioGroup>
            </div>

            <div class='field'>
              <Form.FieldsRadioGroup
                @title='15. Ralentissement psychomoteur'
                @propertyName='14'
                as |Group|
              >
                <Group.FieldRadio
                  @value='0'
                  @label='Vitesse normale de la pensée, des gestes et du discours.'
                />
                <Group.FieldRadio
                  @value='1'
                  @label='Le patient remarque un ralentissement de la pensée et de sa façon de parler.'
                />
                <Group.FieldRadio
                  @value='2'
                  @label="Prend plusieurs secondes pour répondre à la plupart des questions, fait état d'un ralentissement de la pensée."
                />
                <Group.FieldRadio
                  @value='3'
                  @label='Est incapable de répondre à la plupart des questions sans une forte incitation.'
                />
              </Form.FieldsRadioGroup>
            </div>

            <div class='field'>
              <Form.FieldsRadioGroup
                @title='16. Agitation psychomotrice'
                @propertyName='15'
                as |Group|
              >
                <Group.FieldRadio
                  @value='0'
                  @label="Pas d'augmentation de la vitesse ou de désorganisation de la pensée ou des gestes."
                />
                <Group.FieldRadio
                  @value='1'
                  @label="S'agite, se tord les mains et change souvent de position."
                />
                <Group.FieldRadio
                  @value='2'
                  @label='Décrit un besoin irrépressible de bouger et présente une agitation motrice.'
                />
                <Group.FieldRadio
                  @value='3'
                  @label='Incapable de rester assis. Déambule avec ou sans permission.'
                />
              </Form.FieldsRadioGroup>
            </div>

            <div class='ui message' data-test-score>
              <strong>Score : </strong>
              {{#if this.isChangesetValid}}
                {{this.score.number}}
                –
                {{this.score.description}}
              {{else}}
                veuillez remplir tous les champs afin d'obtenir le score.
              {{/if}}
            </div>
          </:firstTab>

          <:secondTab>
            <div class='field'>
              <Form.Date
                @labelName='Date à laquelle le questionnaire a été renseigné'
                @propertyName='occurredAt'
              />
            </div>
            <div class='field'>
              <Form.NumberInput
                @labelName='Score total'
                @propertyName='total'
                @type='integer'
              />
            </div>
          </:secondTab>
        </TwoButtonTabs>
      </:content>
    </Provider>
  </template>
}
